import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './Button';
import { describeError } from '@/lib/describe-error';
import { cn } from '@/lib/utils';

/**
 * The five states every screen handles, as reusable pieces so no screen quietly ships with only
 * the happy path.
 */

/**
 * Skeletons matched to the real layout's dimensions — NOT a centred spinner.
 *
 * A spinner in the middle of an empty page means the content jumps when it arrives, which reads as
 * broken even when it is fast. A skeleton the same shape as the row it replaces means nothing
 * moves.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-chip', className)}
      // Decorative: a screen reader should hear the live region's "loading", not eight empty divs.
      aria-hidden="true"
    />
  );
}

export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-px" role="status" aria-label="Loading campaigns">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-4 bg-surface px-4 py-3.5">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="ml-auto h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

/**
 * An icon, a one-line explanation of what would be here, and the action that creates the first
 * one. An empty table with no guidance is a dead end.
 */
export function EmptyState({
  title,
  description,
  action,
  icon: Icon = Inbox,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  icon?: typeof Inbox;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <div className="rounded-full bg-chip p-3">
        <Icon className="h-6 w-6 text-muted" aria-hidden="true" />
      </div>

      <h2 className="text-base font-semibold text-heading">{title}</h2>
      <p className="max-w-sm text-sm text-muted">{description}</p>

      {action !== undefined && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * What failed, in plain language, plus a retry — and the correlation id, so a user can quote it in
 * a support ticket. Never a raw error object or a stack trace.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const { message, correlationId, isForbidden } = describeError(error);

  return (
    <div
      className={cn('flex flex-col items-center gap-3 px-6 py-16 text-center', className)}
      // Announced, because a user who tabbed away while it loaded never sees it otherwise.
      role="alert"
    >
      <div className="rounded-full bg-danger-bg p-3">
        <AlertTriangle className="h-6 w-6 text-danger-ink" aria-hidden="true" />
      </div>

      <h2 className="text-base font-semibold text-heading">
        {isForbidden ? 'You do not have access to this' : 'Something went wrong'}
      </h2>

      <p className="max-w-md text-sm text-muted">{message}</p>

      {correlationId !== null && (
        <p className="text-xs text-muted">
          Reference <code className="rounded bg-chip px-1 py-0.5 font-mono">{correlationId}</code>
        </p>
      )}

      {/* No retry on a 403 — the role will not change by trying again, and offering one invites
          the user to keep clicking. */}
      {onRetry !== undefined && !isForbidden && (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-2">
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Try again
        </Button>
      )}
    </div>
  );
}

/**
 * A degraded region rather than a blanked page — some data loaded, some failed.
 *
 * Used where one panel's failure should not take the screen with it: the preview failing while the
 * lint findings loaded is still a usable workspace.
 */
export function PartialFailureNotice({ what, error }: { what: string; error: unknown }) {
  const { message } = describeError(error);

  return (
    <div
      className="flex items-start gap-2 border-l-2 border-warning bg-warning-bg px-3 py-2 text-xs text-warning-ink"
      role="status"
    >
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>
        <strong className="font-semibold">{what} could not be loaded.</strong> {message}
      </span>
    </div>
  );
}
